import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "./api";
import useIdleLogout from "./useIdleLogout";

const sectionFields = {
  "Main Member Information": [
    "main_identification_number",
    "main_surname",
    "main_full_names",
    "main_initials",
    "main_title",
    "main_male",
    "main_female",
    "main_date_of_birth",
    "main_employer",
    "main_cell_number",
    "main_home_number",
    "main_work_number",
    "main_fax_number",
    "main_email_address",
    "main_email_statements",
    "main_postal_address_1",
    "main_postal_address_2",
    "main_postal_address_3",
    "main_postal_address_code",
    "main_physical_address_1",
    "main_physical_address_2",
    "main_physical_address_3",
    "main_physical_address_code",
  ],
  "Medical Aid Information": [
    "main_medical_scheme",
    "main_medical_scheme_plan",
    "main_medical_member_number",
    "main_medical_member_main_dep_code",
    "main_medical_gap_cover",
  ],
  "Patient Information": [
    "patient_identification_number",
    "patient_surname",
    "patient_full_names",
    "patient_nick_names",
    "patient_initials",
    "patient_title",
    "patient_male",
    "patient_female",
    "patient_date_of_birth",
    "patient_cell_number",
    "patient_use_appoint_test_results",
    "patient_home_number",
    "patient_work_number",
    "patient_email_address",
    "patient_occupation",
    "patient_marital_status",
    "patient_relationship_main",
    "patient_dep_code",
    "patient_age",
    "patient_height",
    "patient_weight",
    "patient_referring_doctor",
    "patient_referring_doctor_nuiber",
    "patient_gp",
    "patient_gp_number",
  ],
  "Next of Kin Information": [
    "next_kin_initials",
    "next_kin_title",
    "next_kin_surname",
    "next_kin_full_names",
    "next_kin_cell_number",
    "next_kin_relationship",
  ],
};

const formatLabel = (key) => {
  return key
    .replace(/^(main_|patient_|next_kin_)/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

const EditPatient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  const { showWarning, stayLoggedIn } = useIdleLogout();

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    const fetchPatient = async () => {
      try {
        const res = await api.get(`/patients/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFormData(res.data);
        setLoading(false);
      } catch (err) {
        console.error("Error loading patient:", err);
        setMessage("Failed to load patient.");
        setMessageType("error");
      }
    };
    fetchPatient();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      const token = sessionStorage.getItem("token");

      const cleanedFormData = Object.fromEntries(
        Object.entries(formData)
          .filter(([key]) => key !== "signature_image") // Exclude signature_image
          .map(([key, value]) => {
            if (value === undefined || value === null || value === "") {
              return [key, null];
            }
            return [key, value];
          })
      );

      await api.put(`/patients/${id}`, cleanedFormData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setMessage("Changes saved.");
      setMessageType("success");
    } catch (err) {
      console.error("Error saving patient:", err);
      setMessage("Failed to save changes.");
      setMessageType("error");
    }
  };

  if (loading) return <div className="p-6">Loading patient data...</div>;

  return (
    <div
      className="min-h-screen w-full bg-cover bg-center text-gray-800"
      style={{
        backgroundImage: `radial-gradient(circle at top left, #A8A8A8, #8C8C8C, #85D3DE, #8DDFEB)`,
      }}
    >
      <div className="flex justify-center pt-6">
        <img
          src="/InfoMedi_logo.png"
          alt="InfoMedi Logo"
          className="h-20 drop-shadow"
        />
      </div>

      <div className="bg-white/30 backdrop-blur-md shadow-xl rounded-xl px-6 py-8 mt-8 max-w-7xl mx-auto border border-white/40">
        {message && (
          <div
            className={`mb-6 p-3 rounded text-white ${
              messageType === "success" ? "bg-green-600" : "bg-red-600"
            }`}
          >
            {message}
          </div>
        )}

        <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
          <button
            onClick={handleSave}
            className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white w-48 py-2 rounded-lg shadow-md transition-all"
          >
            SAVE CHANGES
          </button>
          <button
            onClick={() => navigate(`/patients/${id}`)}
            className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white w-48 py-2 rounded-lg shadow-md transition-all"
          >
            BACK TO LIST
          </button>
        </div>

        {Object.entries(sectionFields).map(([sectionTitle, keys]) => {
          const half = Math.ceil(keys.length / 2);
          const col1 = keys.slice(0, half);
          const col2 = keys.slice(half);
          return (
            <div key={sectionTitle} className="mb-6">
              <h2 className="text-xl font-bold text-blue-700 border-b border-blue-200 mb-4 pb-1">
                {sectionTitle}
              </h2>
              <div className="grid sm:grid-cols-2 gap-x-12 text-sm text-gray-800">
                {[col1, col2].map((col, i) => (
                  <div key={i} className="flex flex-col gap-4">
                    {col.map((key) => (
                      <div key={key} className="flex flex-col">
                        <label className="text-gray-600">
                          {formatLabel(key)}
                        </label>
                        {[
                          "main_male",
                          "main_female",
                          "patient_male",
                          "patient_female",
                          "main_email_statements",
                          "main_medical_gap_cover",
                        ].includes(key) ? (
                          <input
                            type="checkbox"
                            name={key}
                            checked={!!formData[key]}
                            onChange={handleChange}
                            className="h-5 w-5"
                          />
                        ) : key.includes("date_of_birth") ? (
                          <input
                            type="date"
                            name={key}
                            value={formData[key]?.slice(0, 10) || ""}
                            onChange={handleChange}
                            className="border px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-400"
                          />
                        ) : (
                          <input
                            type="text"
                            name={key}
                            value={formData[key] ?? ""}
                            onChange={handleChange}
                            className="border px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-400"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {showWarning && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 text-yellow-900 border border-yellow-400 px-4 py-2 rounded shadow-lg z-50">
          <p>You will be logged out due to inactivity in 30 seconds.</p>
          <button
            onClick={stayLoggedIn}
            className="mt-2 px-3 py-1 bg-blue-600 text-white rounded"
          >
            Stay Logged In
          </button>
        </div>
      )}
    </div>
  );
};

export default EditPatient;
